function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

export default function (Alpine) {
  Alpine.directive('marquee', (el, { expression }, { evaluate, cleanup }) => {
    const options = expression ? evaluate(expression) : {};
    const speedFactor = options.speedFactor || 20;
    const gap = options.gap || '24px';
    const direction = options.direction || 'normal';
    const enableHover = options.enableHover || false;

    if (!document.getElementById('x-marquee-styles')) {
      const style = document.createElement('style');
      style.id = 'x-marquee-styles';
      style.textContent = `
        @keyframes marquee-motion {
          to { transform: translate3d(calc(-50% - (var(--marquee-gap) / 2)), 0, 0); }
        }
      `;
      document.head.appendChild(style);
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'flex w-fit whitespace-nowrap gap-[var(--marquee-gap)]';

    const content = document.createElement('div');
    content.className = 'min-w-max flex gap-[var(--marquee-gap)]';

    const items = document.createElement('div');
    items.className = 'min-w-max flex gap-[var(--marquee-gap)] items-center';

    while (el.firstChild) {
      items.appendChild(el.firstChild);
    }

    content.appendChild(items);
    wrapper.appendChild(content);
    el.appendChild(wrapper);

    el.classList.add('block', 'w-full', 'overflow-hidden');
    el.style.setProperty('--marquee-gap', gap);
    el.style.setProperty('--marquee-direction', direction);

    const calculateSpeed = () => `${Math.ceil(el.offsetWidth / speedFactor / 2)}s`;

    const setSpeed = (value = calculateSpeed()) => {
      el.style.setProperty('--marquee-speed', value);
      wrapper.style.animation = `marquee-motion var(--marquee-speed) linear infinite var(--marquee-direction)`;
    };

    const calculateNumberOfCopies = () => Math.ceil(el.offsetWidth / items.offsetWidth);

    const duplicateContent = () => {
      const existingClone = wrapper.children[1];
      if (existingClone) existingClone.remove();

      const clone = content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(clone);
    };

    const addRepeatedItems = (numberOfCopies = calculateNumberOfCopies()) => {
      for (let i = 0; i < numberOfCopies - 1; i++) {
        content.appendChild(items.cloneNode(true));
      }
    };

    const handleResize = debounce(() => {
      const newNumberOfCopies = calculateNumberOfCopies();

      if (newNumberOfCopies !== content.children.length) {
        while (content.children.length > 1) {
          content.lastElementChild.remove();
        }
        addRepeatedItems(newNumberOfCopies);
      }

      duplicateContent();
      setSpeed();

      const animation = wrapper.getAnimations()[0];
      if (animation) {
        requestAnimationFrame(() => {
          animation.currentTime = 0;
        });
      }
    }, 250);

    if (enableHover) {
      el.addEventListener('mouseenter', () => {
        const animation = wrapper.getAnimations()[0];
        if (animation) animation.playbackRate = 0;
      });

      el.addEventListener('mouseleave', () => {
        const animation = wrapper.getAnimations()[0];
        if (animation) animation.playbackRate = 1;
      });
    }

    addRepeatedItems();
    duplicateContent();
    setSpeed();

    window.addEventListener('resize', handleResize);

    cleanup(() => {
      window.removeEventListener('resize', handleResize);
    });
  });
}
